/* Send each visitor to the store that actually serves them: Australians to
 * AU, Americans and Canadians to US, British visitors to UK.
 *
 * The AU store outranks the others for most of these terms, so a large share
 * of overseas traffic lands on a site that prices in AUD and cannot ship to
 * them. This moves them once, to the equivalent page, and then never argues
 * with them again. The routes (countries and destination per other store)
 * come from snippets/ak-geo.liquid; this file holds no store list.
 *
 * Three rules govern the whole thing:
 *
 *   1. NEVER redirect a crawler. Googlebot crawls from US IP space, so a
 *      naive geo redirect would bounce it off the AU store and take the AU
 *      rankings with it. That is the single most expensive mistake available
 *      here, so the bot check runs first and fails closed.
 *
 *   2. An explicit choice wins forever. If someone uses the store switcher to
 *      come back to AU, that is a decision, and this must not overrule it on
 *      the next page view. The switcher hands the choice across the origin
 *      boundary in the URL, because localStorage on one store cannot be read
 *      by the other.
 *
 *   3. Only move someone to a page that exists. The counterpart path comes
 *      from the same resolver the hreflang tags and the switcher use, so if a
 *      page has no US equivalent (live foods, most of coral chemistry) nobody
 *      gets dumped on a homepage that does not sell what they came for.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-ak-geo]');
  if (!root) return;

  var HERE = root.getAttribute('data-ak-geo-here') || 'au';

  function countries(list) {
    return String(list || '').split(',')
      .map(function (c) { return c.trim().toUpperCase(); })
      .filter(Boolean);
  }

  /* One route per other store: { region, countries, url }. */
  var ROUTES = [].slice.call(root.querySelectorAll('[data-ak-geo-route]'))
    .map(function (el) {
      return {
        region: (el.getAttribute('data-region') || '').toLowerCase(),
        countries: countries(el.getAttribute('data-countries')),
        url: el.getAttribute('data-url') || ''
      };
    })
    .filter(function (r) { return r.region && r.url && r.countries.length; });

  /* Markup from before routes existed: a single destination on the root. */
  if (!ROUTES.length && root.getAttribute('data-ak-geo-url')) {
    ROUTES.push({
      region: HERE === 'au' ? 'us' : 'au',
      countries: countries(root.getAttribute('data-ak-geo-countries') || 'US,CA'),
      url: root.getAttribute('data-ak-geo-url')
    });
  }

  var CHOICE_KEY = 'ak_region_choice';
  var DONE_KEY = 'ak_geo_moved';

  /* ---- 1. the choice, which outranks everything ------------------------ */

  function store(key, value, session) {
    try { (session ? sessionStorage : localStorage).setItem(key, value); } catch (e) {}
  }
  function read(key, session) {
    try { return (session ? sessionStorage : localStorage).getItem(key); } catch (e) { return null; }
  }

  /* The switcher tags its links with ?ak_region=. That is the handoff across
     origins: the other store cannot read this one's localStorage, so the
     decision travels in the URL and is persisted on arrival. */
  var params = new URLSearchParams(location.search);
  var declared = params.get('ak_region');

  if (declared) {
    store(CHOICE_KEY, declared.toLowerCase());
    /* Take it back out of the address bar. It has done its job, and leaving
       it there would follow the visitor into shares, bookmarks and analytics
       as a second URL for the same page. */
    params.delete('ak_region');
    var q = params.toString();
    try {
      history.replaceState({}, '', location.pathname + (q ? '?' + q : '') + location.hash);
    } catch (e) {}
    return; // arriving on a deliberate choice is never a moment to redirect
  }

  if (read(CHOICE_KEY)) return;      // they have chosen before; leave them alone
  if (read(DONE_KEY, true)) return;  // already moved once this visit

  /* ---- 2. never a crawler ---------------------------------------------- */

  var ua = navigator.userAgent || '';

  /* `bot`, `crawl` and `spider` catch the overwhelming majority including
     Googlebot, bingbot and AdsBot. The named ones are the exceptions that do
     NOT contain any of those words and would otherwise slip through. */
  var BOTS = new RegExp([
    'bot', 'crawl', 'spider', 'slurp', 'archiver', 'preview',
    'google-inspectiontool', 'googleother', 'mediapartners-google', 'apis-google',
    'facebookexternalhit', 'facebookcatalog', 'whatsapp', 'telegram', 'discord',
    'slackbot', 'embedly', 'quora link', 'pinterest', 'redditbot', 'linkedinbot',
    'lighthouse', 'gtmetrix', 'pingdom', 'headlesschrome', 'phantomjs',
    'ahrefs', 'semrush', 'mj12', 'dotbot', 'petal', 'yandex', 'duckduck',
    'applebot', 'amazonbot', 'gptbot', 'claudebot', 'perplexity', 'ccbot', 'bytespider'
  ].join('|'), 'i');

  if (BOTS.test(ua)) return;

  /* No user agent at all is not a browser we recognise, so it is treated as a
     crawler rather than guessed at. */
  if (!ua) return;

  /* An escape hatch for checking the page as a US visitor would see it, and
     for anyone debugging this later. */
  if (/[?&]ak_geo=off/.test(location.search)) return;

  /* ---- 3. somewhere real to send them ---------------------------------- */

  if (!ROUTES.length) return; // this page has no counterpart on any other store

  /* Came from the other store? Then they have just been sent here, or clicked
     through deliberately. Either way, bouncing them back is a loop. */
  if (document.referrer) {
    try {
      var from = new URL(document.referrer).hostname;
      if (from && from !== location.hostname && /aquaculturekings\./i.test(from)) return;
    } catch (e) {}
  }

  /* ---- 4. ask Shopify where they are ----------------------------------- */

  /* Shopify resolves this per request from the visitor's own IP, same origin,
     with no cache headers on the response. A Cloudflare Worker would be the
     other way to do this, but these domains sit on Shopify's Cloudflare zone
     rather than ours, so a Worker would mean a separate hostname and a
     cross-origin round trip for an answer we already have. */
  fetch('/browsing_context_suggestions.json', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin'
  })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      var country = data
        && data.detected_values
        && data.detected_values.country
        && data.detected_values.country.handle;

      if (!country) return;
      country = String(country).toUpperCase();
      var route = null;
      for (var i = 0; i < ROUTES.length; i++) {
        if (ROUTES[i].countries.indexOf(country) !== -1) { route = ROUTES[i]; break; }
      }
      if (!route || route.region === HERE) return;

      /* Mark before leaving. If the other store sends them back for any
         reason, this one will not immediately return serve. */
      store(DONE_KEY, '1', true);
      store(CHOICE_KEY, route.region);

      location.replace(route.url);
    })
    .catch(function () { /* geo unavailable: stay where we are */ });
})();
